import { SkyButton, SkyMessageBar, SkySkeleton, SkySkeletonItem } from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useRef, useState, type ComponentType, type ReactElement } from "react";
import type { DataflowSummary } from "@shared/types/dataflows";
import type { Overview, OverviewSection } from "@shared/types/overview";
import { AlertsCard } from "@/components/overview/AlertsCard";
import { ConnectionsCard } from "@/components/overview/ConnectionsCard";
import { CredentialsCard } from "@/components/overview/CredentialsCard";
import { FirstRunChecklist } from "@/components/overview/FirstRunChecklist";
import { ImportsCard } from "@/components/overview/ImportsCard";
import {
  OverviewCard,
  SectionError,
  sectionState,
  type SectionState,
} from "@/components/overview/OverviewCard";
import { QuietMessageBar } from "@/components/overview/QuietMessageBar";
import { RecentRunsCard } from "@/components/overview/RecentRunsCard";
import { RunnerLastSeen } from "@/components/overview/RunnerLastSeen";
import { RunsCard } from "@/components/overview/RunsCard";
import { SchedulesCard } from "@/components/overview/SchedulesCard";
import { TestsCard, type TestsListState } from "@/components/overview/TestsCard";
import { DataHealthCard } from "@/components/monitor/DataHealthCard";
import { ReliabilityCard } from "@/components/overview/ReliabilityCard";
import {
  useOverview,
  useOverviewConnections,
  useOverviewRecentSteps,
  useOverviewTransformationGraph,
  useOverviewDbtJobs,
} from "@/hooks/use-overview";
import { usePermissions } from "@/hooks/use-permissions";
import { useLiveRunRefresh } from "@/hooks/use-live-run-refresh";
import { isTerminalRun } from "@/components/runs/runFormatting";
import { useMonitorEnabled } from "@/hooks/use-monitor";
import { failureMessage } from "@/lib/api";
import {
  ConnectionsIcon,
  DestinationIcon,
  HealthIcon,
  RetryIcon,
  RunsIcon,
  SchedulesIcon,
  TableObjectIcon,
  TransformationsIcon,
  ValidationSuccessIcon,
  ValidationWarningIcon,
} from "@/lib/icons";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Overview (Session 10, B1): the first screen of every instance role.
 *
 * One read, `GET /api/v2/overview`, re-read every 30 s while the tab is visible. The read is not
 * audited and answers "bound" from the platform row, so this screen never polls an audited read
 * (contract §0.3). Each section arrives on its own terms: a section the role cannot read is
 * hidden, one whose read failed says "Could not load" with Retry, and a number is never a zero
 * that stands in for "unknown".
 *
 * Viewer-safe: the screen writes nothing. Session 11 (owner item 5, "Let's remove the buttons"):
 * the header's quick actions (Run now, Run transformations, New connection) are gone for every
 * role; each card keeps its "Open …" link to the screen that does the work.
 */

function OverviewSkeleton() {
  return (
    <SkySkeleton testId="overview-loading">
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2">
        {[0, 1, 2, 3].map((card) => (
          <div key={card} className="flex flex-col gap-3 rounded-xl border border-border p-4">
            <SkySkeletonItem width={160} />
            <SkySkeletonItem size={40} />
            <SkySkeletonItem size={16} />
          </div>
        ))}
      </div>
    </SkySkeleton>
  );
}

/**
 * The tests card's list state. A list already read stays shown through a later failed re-read;
 * a list never read that failed is "error", not an empty list; a role without
 * `dbt_jobs.read` gets no list at all (the read is never sent).
 */
function testsListState(
  canRead: boolean,
  list: ReturnType<typeof useOverviewDbtJobs>,
): TestsListState {
  if (!canRead) return "hidden";
  if (list.data !== undefined) return "ready";
  if (list.isError) return "error";
  return "loading";
}

/** Each section's card icon (round 4), for the "Could not load" card that stands in for it. */
const SECTION_ICONS: Record<OverviewSection, ComponentType<{ className?: string }>> = {
  destination: DestinationIcon,
  connections: ConnectionsIcon,
  schedules: SchedulesIcon,
  runs: RunsIcon,
  imports: TableObjectIcon,
  tests: TransformationsIcon,
  alerts: ValidationWarningIcon,
  credentials: ValidationSuccessIcon,
  runner: HealthIcon,
};

/** Each section's card title, so a card whose read failed still says which card it is. */
const SECTION_TITLES: Record<OverviewSection, string> = {
  destination: "Destination",
  connections: "Connections",
  schedules: "Schedules",
  runs: "Runs, last 24 hours",
  imports: "Rows imported, last 24 hours",
  tests: "Transformation tests",
  alerts: "Alerts",
  credentials: "Credentials",
  runner: "Runner",
};

/**
 * #2005: a run is in flight on the Overview: the Running now tile counts one, or a recent run is
 * still queued or running.
 */
function overviewHasActiveRun(data: Overview | undefined): boolean {
  const runs = data?.runs;
  if (!runs) return false;
  if (runs.active && runs.active.running + runs.active.queued > 0) return true;
  return runs.recent.some((row) => !isTerminalRun(row.status));
}

export default function OverviewPage() {
  const query = useOverview();
  // #2005: the cards follow a run's start and finish within seconds. The page holds the tab's
  // live feed, so the run's frames re-read the overview, the Mine runs and the Reliability card at
  // once. The overview polls itself every 30 s, and the flip to nothing in flight refreshes
  // everything a finish touches, so the hook needs no poll of its own here.
  useLiveRunRefresh({ active: overviewHasActiveRun(query.data) });
  return (
    <div className="flex flex-col gap-4" data-testid="overview-page">
      <OverviewBody query={query} />
    </div>
  );
}

/**
 * One section's card: the card while its read is good and it has data, the error card while its
 * read failed, nothing otherwise. Called in place, so each slot holds the element it always did.
 */
function sectionSlot<T>(
  state: SectionState,
  data: T | null | undefined,
  render: (data: T) => ReactElement,
  errorCard: () => ReactElement | null,
): ReactElement | null {
  if (state === "ok" && data) return render(data);
  return state === "error" ? errorCard() : null;
}

/** A re-read failed after a good one: the numbers below are the last good answer, and say so. */
function StaleBar({
  updatedAt,
  zone,
  retrying,
  onRetry,
}: {
  updatedAt: number;
  zone: string;
  retrying: boolean;
  onRetry: () => void;
}) {
  return (
    <QuietMessageBar
      intent="warning"
      layout="multiline"
      message={`The last refresh failed, so these numbers are from ${formatInstant(
        new Date(updatedAt).toISOString(),
        zone,
        "short",
        { named: true },
      )}.`}
      testId="overview-stale"
      actions={
        <SkyButton
          size="small"
          icon={RetryIcon}
          loading={retrying}
          onClick={onRetry}
          testId="overview-stale-retry"
        >
          Retry
        </SkyButton>
      }
    />
  );
}

function sectionOk(overview: Overview | undefined, section: OverviewSection): boolean {
  return overview ? sectionState(overview, section) === "ok" : false;
}

/** The cards, two to a row where there is room; each section's card or its error card. */
function OverviewGrid({
  overview,
  errorCard,
  tests,
  dataHealthVisible,
  reliabilityVisible,
  connectionNames,
  connectionRows,
}: {
  overview: Overview;
  errorCard: (section: OverviewSection) => ReactElement;
  tests: {
    transformations: ReturnType<typeof useOverviewDbtJobs>;
    graph: ReturnType<typeof useOverviewTransformationGraph>;
    recentSteps: ReturnType<typeof useOverviewRecentSteps>;
    canReadDbtJobs: boolean;
  };
  dataHealthVisible: boolean;
  /** `runs.read`: the Reliability card (#828), whatever the skyMonitor flag says. */
  reliabilityVisible: boolean;
  connectionNames: ReadonlyMap<string, string> | null;
  /** #1212: the Connections list's rows, once read; the card counts them in the list's words. */
  connectionRows: readonly DataflowSummary[] | undefined;
}) {
  const state = (section: OverviewSection): SectionState => sectionState(overview, section);
  const { transformations, graph, recentSteps, canReadDbtJobs } = tests;
  return (
    <div
      className="grid grid-cols-[minmax(0,1fr)] gap-4 md:grid-cols-2"
      data-testid="overview-grid"
    >
      {sectionSlot(
        state("connections"),
        overview.connections,
        (data) => (
          <ConnectionsCard connections={data} list={connectionRows} />
        ),
        () => errorCard("connections"),
      )}

      {sectionSlot(
        state("schedules"),
        overview.schedules,
        (data) => (
          <SchedulesCard schedules={data} />
        ),
        () => errorCard("schedules"),
      )}

      {sectionSlot(
        state("runs"),
        overview.runs,
        (data) => (
          <RunsCard runs={data} />
        ),
        () => errorCard("runs"),
      )}

      {sectionSlot(
        state("imports"),
        overview.imports,
        (data) => (
          <ImportsCard imports={data} />
        ),
        () => errorCard("imports"),
      )}

      {sectionSlot(
        state("tests"),
        overview.tests,
        (data) => (
          <TestsCard
            tests={data}
            transformations={transformations.data}
            graph={graph.data}
            recentSteps={recentSteps.data}
            listState={testsListState(canReadDbtJobs, transformations)}
            onRetryList={() => void transformations.refetch()}
            retryingList={transformations.isFetching}
          />
        ),
        () => errorCard("tests"),
      )}

      {sectionSlot(
        state("credentials"),
        overview.credentials,
        (data) => (
          <CredentialsCard credentials={data} />
        ),
        () => errorCard("credentials"),
      )}

      {/* #828 (Level B): beside Data health, and shown whether skyMonitor is on or not. */}
      {reliabilityVisible ? <ReliabilityCard /> : null}

      {dataHealthVisible ? <DataHealthCard /> : null}

      {sectionSlot(
        state("alerts"),
        overview.alerts,
        (data) => (
          <AlertsCard alerts={data} runs={state("runs") === "ok" ? overview.runs : null} />
        ),
        () => errorCard("alerts"),
      )}

      {state("runs") === "ok" && overview.runs ? (
        <div className="min-w-0 md:col-span-2">
          <RecentRunsCard rows={overview.runs.recent} connectionNames={connectionNames} />
        </div>
      ) : null}
    </div>
  );
}

function OverviewBody({ query }: { query: ReturnType<typeof useOverview> }) {
  const zone = useDisplayTz();
  const { canAny } = usePermissions();
  const overview = query.data;

  const testsVisible = sectionOk(overview, "tests");
  const recentVisible = sectionOk(overview, "runs");
  const canReadDbtJobs = testsVisible && canAny("dbt_jobs.read");
  const transformations = useOverviewDbtJobs(canReadDbtJobs);
  const graph = useOverviewTransformationGraph(canReadDbtJobs);
  const recentSteps = useOverviewRecentSteps(canReadDbtJobs, overview?.tests?.latest ?? null);
  const connectionsVisible = sectionOk(overview, "connections");
  const connectionList = useOverviewConnections(
    (recentVisible || connectionsVisible) && canAny("dataflows.read"),
  );
  // skyMonitor's tile: behind its flag, for whoever reads data health.
  const dataHealthVisible = useMonitorEnabled() && canAny("monitor.read");
  const reliabilityVisible = canAny("runs.read");

  const connectionNames = useMemo(
    () =>
      connectionList.data
        ? new Map(connectionList.data.map((row) => [row.id, row.displayName]))
        : null,
    [connectionList.data],
  );

  // Retry spins only for a Retry the user pressed, never for the 30 s background re-read.
  const [retryPending, setRetryPending] = useState(false);
  // The section whose Retry was pressed, and how far the answers had got when it was. When the
  // re-read brings the section back, its error card and the focused Retry unmount, and focus
  // would fall to <body>; it moves to the card's heading instead. The next answer is told by
  // `dataUpdatedAt` / `errorUpdateCount`, not by the refetch promise: that settles before the
  // observer re-renders with what it brought.
  const focusAfterRetry = useRef<{
    section: OverviewSection;
    dataUpdatedAt: number;
    errorUpdateCount: number;
  } | null>(null);
  const retry = (section?: OverviewSection) => {
    focusAfterRetry.current = section
      ? { section, dataUpdatedAt: query.dataUpdatedAt, errorUpdateCount: query.errorUpdateCount }
      : null;
    setRetryPending(true);
    void query.refetch().finally(() => setRetryPending(false));
  };
  const retrying = retryPending && query.isFetching;

  const { dataUpdatedAt, errorUpdateCount } = query;
  useEffect(() => {
    const pending = focusAfterRetry.current;
    if (pending === null || !overview) return;
    const answered =
      dataUpdatedAt > pending.dataUpdatedAt || errorUpdateCount > pending.errorUpdateCount;
    if (!answered) return;
    focusAfterRetry.current = null;
    if (sectionState(overview, pending.section) !== "ok") return;
    // Only when focus was lost: a user who moved on keeps focus where they put it.
    const active = document.activeElement;
    if (active !== null && active !== document.body) return;
    document.getElementById(`overview-${pending.section}-title`)?.focus();
  }, [overview, dataUpdatedAt, errorUpdateCount]);

  if (query.isLoading) return <OverviewSkeleton />;

  if (!overview) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        title="Could not load the overview"
        message={failureMessage(query.error, "The overview could not be read. Try again.")}
        testId="overview-error"
        actions={
          <SkyButton size="small" icon={RetryIcon} loading={retrying} onClick={() => retry()}>
            Retry
          </SkyButton>
        }
      />
    );
  }

  const state = (section: OverviewSection): SectionState => sectionState(overview, section);
  const errorCard = (section: OverviewSection) => (
    <OverviewCard
      key={section}
      id={section}
      title={SECTION_TITLES[section]}
      icon={SECTION_ICONS[section]}
      tone="destructive"
      testId={`overview-${section}`}
    >
      <SectionError section={section} onRetry={retry} retrying={retrying} />
    </OverviewCard>
  );

  const firstRun = state("connections") === "ok" && overview.connections?.total === 0;
  // A re-read failed after a good one: the numbers below are the last good answer, and say so.
  const stale = query.isError;

  return (
    <>
      {stale ? (
        <StaleBar
          updatedAt={query.dataUpdatedAt}
          zone={zone}
          retrying={retrying}
          onRetry={() => retry()}
        />
      ) : null}

      {state("destination") === "error" ? errorCard("destination") : null}

      {/* Owner answer (f): the runner's last heartbeat, a warning past ten minutes. A failed
          read of it says nothing rather than a false alarm; the other cards still answer. */}
      {sectionSlot(
        state("runner"),
        overview.runner,
        (runner) => (
          <RunnerLastSeen runner={runner} />
        ),
        () => null,
      )}

      {firstRun ? (
        <FirstRunChecklist
          destination={state("destination") === "ok" ? overview.destination : null}
        />
      ) : (
        // Round 6 walk W1: never `grid-cols-1` here. skypoint-ui ships that class after the
        // app's `md:grid-cols-2`, so the grid kept one explicit column, the last runs card's
        // `md:col-span-2` opened an implicit `auto` column sized to its cards' longest line, and
        // the first column shrank to 0 px at 1280 and 1440 (`libraryCascade.test.ts`).
        <OverviewGrid
          overview={overview}
          errorCard={errorCard}
          tests={{ transformations, graph, recentSteps, canReadDbtJobs }}
          dataHealthVisible={dataHealthVisible}
          reliabilityVisible={reliabilityVisible}
          connectionNames={connectionNames}
          connectionRows={connectionList.data}
        />
      )}
    </>
  );
}
